import { App as CapApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { v4 } from 'uuid';
import type { AuthStatus } from 'Utils/AuthService';
import type { SittingStartReason } from 'Types/EventTypes';

// An app sitting is one stretch of app use, named by the analytics session id
// every event carries. The client starts a new one on sign-in, on sign-out, and on
// return after the app was hidden for at least this long, so a phone resumed the
// next day does not add its events to yesterday's sitting.
export const SITTING_HIDDEN_LIMIT_MS = 30 * 60 * 1000;

// The new sitting an auth change starts, or null when it starts none. A session
// found at app start (resolving -> authenticated) is the cold start's own sitting.
export const sittingStartForAuthChange = (
  previous: AuthStatus,
  next: AuthStatus,
): SittingStartReason | null => {
  if (previous === 'unauthenticated' && next === 'authenticated') return 'signed_in';
  if (previous === 'authenticated' && next === 'unauthenticated') return 'signed_out';
  return null;
};

// When the app went out of sight. The first hide signal counts (hidden, then
// pagehide or backgrounded, may all fire), and the first show signal answers
// whether the app was away long enough for a new sitting.
export const createHiddenTracker = (hiddenLimitMs: number = SITTING_HIDDEN_LIMIT_MS) => {
  let hiddenAt: number | null = null;
  return {
    hidden(now: number): void {
      if (hiddenAt === null) hiddenAt = now;
    },
    visible(now: number): boolean {
      const wasHiddenAt = hiddenAt;
      hiddenAt = null;
      return wasHiddenAt !== null && now - wasHiddenAt >= hiddenLimitMs;
    },
  };
};

export type VisibilityListeners = { onHidden: () => void; onVisible: () => void };

export type SittingRotationOptions = {
  auth: {
    getSnapshot: () => { status: AuthStatus };
    subscribe: (listener: () => void) => () => void;
  };
  // Hooks the platform's hide and show signals up to the two listeners; returns
  // the unsubscribe.
  listenForVisibility: (listeners: VisibilityListeners) => () => void;
  startNewSitting: (reason: SittingStartReason) => void;
  now?: () => number;
};

// Starts a new sitting whenever one of the three rules says so. Returns the
// unsubscribe.
export const startSittingRotation = ({
  auth,
  listenForVisibility,
  startNewSitting,
  now = () => Date.now(),
}: SittingRotationOptions): (() => void) => {
  let status = auth.getSnapshot().status;
  const unsubscribeAuth = auth.subscribe(() => {
    const next = auth.getSnapshot().status;
    const reason = sittingStartForAuthChange(status, next);
    status = next;
    if (reason) startNewSitting(reason);
  });
  const tracker = createHiddenTracker();
  const unsubscribeVisibility = listenForVisibility({
    onHidden: () => tracker.hidden(now()),
    onVisible: () => {
      if (tracker.visible(now())) startNewSitting('resumed');
    },
  });
  return () => {
    unsubscribeAuth();
    unsubscribeVisibility();
  };
};

// The app's hide and show signals: the tab's visibility on the web, and the native
// app going to the background and back. Returns the unsubscribe.
export const listenForAppVisibility = ({ onHidden, onVisible }: VisibilityListeners) => {
  const onVisibilityChange = () => (document.hidden ? onHidden() : onVisible());
  document.addEventListener('visibilitychange', onVisibilityChange);
  let appStateHandle: PluginListenerHandle | undefined;
  let cancelled = false;
  CapApp.addListener('appStateChange', ({ isActive }) => (isActive ? onVisible() : onHidden()))
    .then((handle) => {
      if (cancelled) void handle.remove();
      else appStateHandle = handle;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    void appStateHandle?.remove();
  };
};

// A new sitting id: a uuid v4, the only kind the server links a sitting by.
export const newSittingId = (): string => v4();
